import {
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  Typography,
  Theme,
  makeStyles
} from '@material-ui/core';
import { isEmpty } from 'lodash';
import { useObserver } from 'mobx-react-lite';
import React, { useState, FC, Fragment } from 'react';
import { isNullOrUndefined } from 'util';
import {
  formatSelectionLevel,
  isRequiredSelectionLevel,
  maxOptionCountForSelectionLevel
} from '../../../domain/models/SelectionLevel';
import OptionPrice from './OptionPrice';
import { OrderingModifier, OrderModifier, OrderingOption } from 'kiolyn-api';
import newOrderOption from '../../../domain/usecases/NewOrderOption';
import EditSubModifier from './EditSubModifier';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    height: 'calc(100% - 96px)'
  },
  title: {
    marginRight: 48
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  price: {
    color: theme.palette.primary.main
  },
  selectOptionPrompt: {
    marginTop: theme.spacing(2),
    fontSize: '0.9rem',
    textAlign: 'center'
  },
  chooseOne: {
    fontSize: '13px',
    fontStyle: 'italic'
  }
}));

interface EditModifierOptionsProps {
  modifiers?: OrderModifier[];
  allModifiers?: OrderingModifier[];
  onChange?: (modifiers: OrderModifier[]) => void;
}

const EditModifierOptions: FC<EditModifierOptionsProps> = ({
  modifiers,
  allModifiers,
  onChange
}) => {
  const classes = useStyles();

  // Hold the current selected tab index
  const [tab, setTab] = useState(0);

  // Hold the current modifier bound with current tab
  const [modifier, setModifier] = useState(
    isNullOrUndefined(allModifiers) || isEmpty(allModifiers)
      ? undefined
      : allModifiers[0]
  );

  // Hold the message to prompt selection
  const [message, setMessage] = useState(
    isNullOrUndefined(modifier)
      ? ''
      : formatSelectionLevel(modifier.selection_level)
  );

  // Hold the current option that user clicks on, to show the sub modifier
  const [currentOption, setOption] = useState<OrderingOption | undefined>(
    undefined
  );

  const tabChange = (_e: any, newValue: number) => {
    if (isNullOrUndefined(allModifiers) || isEmpty(allModifiers)) {
      return;
    }

    setTab(newValue);
    const modifier = allModifiers[newValue];
    setModifier(modifier);
    setMessage(formatSelectionLevel(modifier.selection_level));
  };

  const toggleSelection = (option: OrderingOption) => {
    if (isNullOrUndefined(modifier)) {
      return;
    }

    const newModifiders = modifiers || [];
    const selectedModifier = newModifiders.find(m => m.id === modifier.id);
    if (isNullOrUndefined(selectedModifier)) {
      // If modifier is not yet added, then add it

      newModifiders.push({
        id: modifier.id,
        type: 'order-modifier',
        name: modifier.name || '',
        prefix: modifier.prefix || '',
        options: [newOrderOption(option)],
        sameline: modifier.sameline || false
      });
      // Set current option to show sub modifier
      setOption(option);
    } else {
      // Try to find the matching option
      const selectedOption = selectedModifier.options.find(
        o => o.id === option.id
      );
      if (isNullOrUndefined(selectedOption)) {
        // If not yet added then handle it according to selection_level
        const maxCount = maxOptionCountForSelectionLevel(
          modifier.selection_level
        );
        // If the selection_level has a max_count
        // and the number of selected options already reach the max_count
        // then remove the first selected one
        if (maxCount > 0 && selectedModifier.options.length === maxCount) {
          selectedModifier.options.shift();
        }
        selectedModifier.options.push(newOrderOption(option));
        // Set current option to show sub modifier
        setOption(option);
      } else {
        // If it is already added, then remove it
        selectedModifier.options = selectedModifier.options.filter(
          o => o.id !== option.id
        );
        // Remove option when unselecting it
        setOption(undefined);
      }
    }

    if (!isNullOrUndefined(onChange)) {
      onChange(newModifiders);
    }
  };

  // Return true is an ordering option should be selected
  const isSelected = (option: OrderingOption): boolean => {
    if (
      isNullOrUndefined(modifier) ||
      isNullOrUndefined(modifiers) ||
      isEmpty(modifiers)
    ) {
      return false;
    }
    const selectedModifier = modifiers.find(m => m.id === modifier.id);
    if (
      isNullOrUndefined(selectedModifier) ||
      isNullOrUndefined(selectedModifier.options)
    ) {
      return false;
    }
    return !isNullOrUndefined(
      selectedModifier.options.find(o => o.id === option.id)
    );
  };

  return useObserver(() => {
    if (isNullOrUndefined(allModifiers) || isEmpty(allModifiers)) {
      return null;
    }
    return (
      <Box>
        <Tabs
          value={tab}
          onChange={tabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          aria-label="list of modifiers"
        >
          {allModifiers.map(modifier => (
            <Tab
              label={`${modifier.name}${
                isRequiredSelectionLevel(modifier.selection_level) ? ' *' : ''
              }`}
              key={modifier.id}
            />
          ))}
        </Tabs>
        <Divider></Divider>

        {!isEmpty(message) && (
          <Typography
            variant="body1"
            color="error"
            className={classes.selectOptionPrompt}
          >
            Please select {message} below
          </Typography>
        )}
        {!isNullOrUndefined(modifier) && !isNullOrUndefined(modifier.options) && (
          <List>
            {modifier.options.map(option => (
              <Fragment key={option.id}>
                {isEmpty(option.out_of_stock) ? (
                  <ListItem
                    button
                    divider
                    onClick={() => toggleSelection(option)}
                  >
                    <ListItemText
                      primary={`${
                        isNullOrUndefined(modifier.prefix)
                          ? ''
                          : `${modifier.prefix} `
                      }${option.name}`}
                      secondary={
                        <Fragment>
                          {option.required_level === 'exact_one' &&
                            isSelected(option) && (
                              <Typography
                                component="span"
                                color="error"
                                className={classes.chooseOne}
                              >
                                Please choose one option <br />
                              </Typography>
                            )}
                          <OptionPrice price={option.price} />
                        </Fragment>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Checkbox
                        edge="end"
                        color="primary"
                        checked={isSelected(option)}
                        onChange={() => toggleSelection(option)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                ) : (
                  <ListItem divider>
                    <ListItemText
                      primary={`${
                        isNullOrUndefined(modifier.prefix)
                          ? ''
                          : `${modifier.prefix} `
                      }${option.name}`}
                      secondary={
                        <Fragment>
                          <OptionPrice price={option.price} />
                          <br />
                          <Typography component="span" color="error">
                            Out of stock {option.out_of_stock}
                          </Typography>
                        </Fragment>
                      }
                    />
                  </ListItem>
                )}
                {currentOption !== undefined &&
                  currentOption.id === option.id && (
                    <EditSubModifier
                      modifier={modifier}
                      option={currentOption}
                      selectedModifiers={modifiers}
                      onChange={onChange}
                    />
                  )}
              </Fragment>
            ))}
          </List>
        )}
      </Box>
    );
  });
};

export default EditModifierOptions;
